import { ErrorMessage } from '@hookform/error-message';
import { useFormContext } from 'react-hook-form';
import type {
  FieldErrors,
  RegisterOptions,
  UseFormReturn,
} from 'react-hook-form';
import { useRef, useEffect, useState, useContext } from 'react';
import { useTranslation } from '@/utils/i18n/useTranslation';
import RichTextBlock from '@/components/RichTextBlock/RichTextBlock';

import convertDataToTranslation from '@/utils/data/convertDataToTranslation';

import FormError from '@/components/FormError/FormError';
import Svg from '@/components/Svg/Svg';
import { UserContext } from '@/context/UserContext';
import type {
  FormItemComponentProps,
  FormItemRequiredState,
  FormRichText,
  TranslateFn,
} from '@/types/domain';

// `errors` is read off the context but RHF exposes it on `formState.errors`; see FormItemTextfield.
type FormContextWithErrors = UseFormReturn & { errors?: FieldErrors };

const revealClass = ' is-revealed';
const activeClass = ' is-active';
const requiredHint = ' *';

const FormItemCheckbox = (props: FormItemComponentProps) => {
  const {
    item,
    onFormItemChange,
    onFormItemLoad,
    onGroupValidation,
    groupedState,
    revealState,
  } = props;
  const { register, watch, errors, formState, trigger } =
    useFormContext() as unknown as FormContextWithErrors;
  const userContext = useContext(UserContext);

  /* eslint-disable @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove */
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT,
  );
  /* eslint-enable @typescript-eslint/no-unnecessary-condition */
  const requiredErrorMessage = t('FORMITEM__ERROR_REQUIRED');

  const [isRequired, setIsRequired] = useState<FormItemRequiredState>({
    required: false,
  });
  const [isGrouped, setIsGrouped] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const itemRef = useRef<HTMLDivElement>(null);
  const itemProps = item;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const isChecked = watch(item.name!) as boolean;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
  const itemInputRef = register(item.name!, isRequired as RegisterOptions);

  useEffect(() => {
    if (!item.revealed && item.required)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setIsRequired({ required: requiredErrorMessage });
    /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    if (!item.revealed && item.requiredGroup)
      setIsRequired({
        required: false,
        validate: onGroupValidation!({ itemProps }),
      });
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, []);

  // eslint-disable-next-line react-hooks/immutability -- intentional mutation of a render/prop/state value; cloning would change identity/behavior
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (isChecked === undefined || isChecked === null) return;
    // eslint-disable-next-line react-hooks/immutability -- intentional mutation of a render/prop/state value; cloning would change identity/behavior
    if (isLoaded) itemProps.checked = isChecked;
    /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    if (!isLoaded && onFormItemLoad)
      onFormItemLoad({ itemProps, itemRef, itemValue: isChecked });
    else if (onFormItemLoad)
      onFormItemChange!({ itemProps, itemRef, itemValue: isChecked });
    if (isLoaded) void trigger();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
    else if (!isLoaded) setIsLoaded(true);
    /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [isChecked]);

  useEffect(() => {
    if (item.revealed && item.required)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs form-item state from RHF/props, which is not render-derivable
      setIsRequired({ required: revealState ? requiredErrorMessage : false });
    /* eslint-disable @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
    if (item.revealed && item.requiredGroup)
      setIsRequired({
        required: false,
        validate: onGroupValidation!({ itemProps }),
      });
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    void trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [revealState]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    if (!(item?.group && groupedState)) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- optimistic GraphQL type — this guard defends a null the generated type doesn't model; re-type from codegen to remove
    const itemLabel = item?.group?.label;
    const groupedItem = groupedState.find((str) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
      return str.label.includes(itemLabel!);
    });
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, react-hooks/set-state-in-effect -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove
    setIsGrouped(groupedItem!.active);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RHF form-item lifecycle effect; intentional trigger (adding the recreated onFormItem* callbacks / prop values would loop)
  }, [groupedState]);

  /* eslint-disable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition -- value is present by contract here but the GraphQL type is nullable; re-type from codegen to remove */
  return (
    <div
      ref={itemRef}
      className={`FormItem FormItem--${String(item.type)}${item.modifier ? ` FormItem--${item.modifier}` : ''}${
        revealState ? revealClass : ''
      }${isGrouped ? activeClass : ''}`}
      data-reveal-target={item.revealed}
    >
      <label className="FormItem-checkboxLabel" htmlFor={item.name}>
        <input
          type="checkbox"
          defaultChecked={item.checked}
          id={item.name}
          name={item.name}
          className={`FormItem-input FormItem-input--${String(item.type)}`}
          onChange={(e) => {
            void itemInputRef.onChange(e);
          }}
          ref={itemInputRef.ref}
        />
        <div className="FormItem-checkboxIndicator">
          <Svg id="icon-check" modifiers={['FormItem-checkboxIcon']} />
        </div>
        {item.richText ? (
          <RichTextBlock
            content={
              !(item.richText as FormRichText).data && item.richText
                ? item.transl
                  ? t(item.richText as string)
                  : item.richText
                : ''
            }
            data={
              (item.richText as FormRichText).data
                ? (item.richText as FormRichText).data &&
                  (item.richText as FormRichText).transl
                  ? convertDataToTranslation(
                      (item.richText as FormRichText).data as Parameters<
                        typeof convertDataToTranslation
                      >[0],
                      t as TranslateFn,
                    )
                  : (item.richText as FormRichText).data
                : []
            }
            modifiers={['RichTextBlock--modalContent']}
          />
        ) : (
          <>{String(item.transl ? t(item.transl as string) : item.label)}</>
        )}
        {item.required ? requiredHint : ''}
      </label>
      {formState.isSubmitted && (
        <ErrorMessage errors={errors} name={item.name!} as={<FormError />} />
      )}
    </div>
  );
  /* eslint-enable @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-unnecessary-condition */
};

export default FormItemCheckbox;
